<template>
  <div class="wheel-participation">
    <h2>好友大转盘</h2>
    <p v-if="!loggedIn">登录后参与，查看本人抽奖次数和提现进度。</p>
    <ActivityAction v-if="!loggedIn" @click="login">登录后参与</ActivityAction>
    <template v-else>
      <ErrorNotice v-if="error" :message="error"></ErrorNotice>
      <p v-if="loading && !state" role="status">正在查询转盘进度…</p>
      <p v-if="busy" role="status" aria-live="polite">正在处理，请稍候，勿重复点击。</p>
      <p v-else-if="actionMessage" role="status" aria-live="polite">{{ actionMessage }}</p>
      <template v-if="state">
        <p>{{ statusLabel }}</p>
        <template v-if="state.status !== 'NOT_JOINED'">
          <div class="wheel-summary">
            <p>{{ ['PENDING', 'PAID', 'REJECTED'].includes(state.status) ? '本次提现' : '累计奖励' }} {{ state.withdrawableAmount }}{{ state.rewardValueType === 2 ? '积分' : '元' }}</p>
            <p>提现进度 {{ state.withdrawalProgress }}%</p>
            <p>有效新用户 {{ state.actualNewInviteeCount }}/{{ state.requiredNewInviteeCount }}人</p>
            <p>剩余抽奖 {{ state.remainingDrawCount }}次</p>
          </div>
          <p v-if="state.expiresAt">本轮截止 {{ state.expiresAt }}</p>
          <div class="wheel-prizes"><p v-for="prize in state.prizes" :key="prize.sequence">{{ prize.prizeName }}</p></div>
          <p v-if="state.lastPrizeName">最近抽奖：{{ state.lastPrizeName }} {{ state.lastPrizeContent }}</p>
        </template>
        <ActivityAction v-if="state.status === 'NOT_JOINED'" :disabled="busy || loading || Boolean(error)" :loading="busy && busyAction === 'join'" @click="act('join')">参与活动</ActivityAction>
        <template v-if="state.status === 'ACTIVE'">
          <ActivityAction :disabled="busy || loading || Boolean(error)" :loading="busy && busyAction === 'tasks'" @click="act('tasks')">领取今日次数并核对任务</ActivityAction>
          <ActivityAction :disabled="busy || loading || Boolean(error) || state.remainingDrawCount < 1" :loading="busy && busyAction === 'draw'" @click="act('draw')">抽奖（消耗1次）</ActivityAction>
          <div v-if="state.walletType === 2 && state.status === 'ACTIVE'" class="wheel-wallet">
            <label for="wheel-wallet">提现游戏钱包</label>
            <select id="wheel-wallet" v-model="walletCode" :disabled="busy">
              <option value="" disabled>请选择游戏钱包</option>
              <option v-for="wallet in state.walletOptions || []" :key="wallet.code" :value="wallet.code">{{ wallet.name }}</option>
            </select>
            <p v-if="!state.walletOptions?.length">请先在游戏钱包页面开通钱包，再刷新进度。</p>
          </div>
          <p v-if="state.qualificationMessage">{{ state.qualificationMessage }}</p>
          <ActivityAction v-if="state.canWithdraw" :disabled="busy || loading || Boolean(error) || (state.walletType === 2 && !walletCode)" :loading="busy && busyAction === 'withdraw'" @click="act('withdraw')">申请统一提现</ActivityAction>
          <p v-if="!state.qualificationMessage">新用户完善资料并完成首存后，计入有效邀请；奖励达标审核后统一发放。</p>
        </template>
        <div class="wheel-assist">
          <label for="wheel-target">好友账号</label>
          <input id="wheel-target" v-model="target" maxlength="64" placeholder="填写要助力的好友账号" :disabled="busy" />
          <ActivityAction :disabled="busy || loading || Boolean(error) || !target.trim()" :loading="busy && busyAction === 'assist'" @click="act('assist')">为好友助力</ActivityAction>
          <p v-if="assistMessage">{{ assistMessage }}</p>
        </div>
      </template>
      <ActivityAction variant="secondary" :disabled="busy" :loading="loading" @click="load">刷新进度</ActivityAction>
    </template>
  </div>
</template>
<script setup>
import ErrorNotice from './feedback/ErrorNotice.vue'
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { friendWheelApi } from '../services/webApi';
import { authStorage } from '../services/http';
import { useWebActivity } from '../services/activities/webAdapter.js';
import ActivityAction from './activities/ActivityAction.vue';
const { openAuth, isLoggedIn, getAuth, notify } = useWebActivity();
const getToken = () => authStorage.getToken();
import { createFriendWheelActions } from '../services/friendWheelActions';
const props = defineProps({ activityId: { type: [Number, String], required: true } });
const emit = defineEmits(['busy']);
const walletCode = ref('');
const busyAction = ref('');
const actionMessage = ref('');
const state = ref(null), error = ref(''), loading = ref(false), busy = ref(false), target = ref(''), assistMessage = ref(''), loggedIn = ref(Boolean(getToken()));
const run = createFriendWheelActions(friendWheelApi);
let sequence = 0, loadedKey = '';
const statusLabel = computed(() => ({ NOT_JOINED: '尚未参与', ACTIVE: '本轮进行中', PENDING: '提现待审核', PAID: '提现已发放', REJECTED: '提现审核未通过', EXPIRED: '本轮已过期' })[state.value?.status] || '');
function login() { openAuth('login'); }
async function load() {
  const token = getToken(), id = props.activityId, key = `${id}:${token}:${getAuth()?.memberId || ''}`;
  if (loading.value && loadedKey === key) return;
  const current = ++sequence;
  loggedIn.value = Boolean(token); error.value = ''; actionMessage.value = '';
  if (loadedKey !== key) { state.value = null; target.value = ''; walletCode.value = ''; assistMessage.value = ''; }
  loadedKey = key;
  if (!token) { loading.value = false; return; }
  loading.value = true;
  try { const result = await friendWheelApi.state(id); if (current === sequence && token === getToken()) state.value = result; }
  catch (cause) { if (current === sequence && token === getToken()) { error.value = cause.message || '转盘进度查询失败'; notify?.(error.value, 'error'); } }
  finally { if (current === sequence) loading.value = false; }
}
async function act(action) {
  if (busy.value || loading.value || error.value || !getToken() || !state.value) return;
  const token = getToken(), current = sequence, username = state.value.memberUsername, id = props.activityId;
  busy.value = true; busyAction.value = action; error.value = ''; actionMessage.value = ''; assistMessage.value = '';
  try {
    const result = await run(action, id, username, target.value.trim(), walletCode.value);
    if (current !== sequence || token !== getToken()) return;
    if (action === 'assist') assistMessage.value = `已核对 ${result.memberUsername} 的助力进度`;
    else {
      state.value = { ...result, walletOptions: result.walletOptions?.length ? result.walletOptions : state.value?.walletOptions || [] };
      if (action === 'join') { await load(); if (token !== getToken() || id !== props.activityId) return; actionMessage.value = '参与成功'; }
      else if (action === 'tasks') actionMessage.value = `今日次数已核对，剩余 ${result.remainingDrawCount} 次`;
      else if (action === 'draw') actionMessage.value = `本次抽中 ${result.lastPrizeName}，剩余 ${result.remainingDrawCount} 次`;
      else if (action === 'withdraw') actionMessage.value = '提现申请已提交，等待审核';
    }
    notify?.(action === 'assist' ? assistMessage.value : actionMessage.value, 'success');
  } catch (cause) { if (current === sequence && token === getToken()) { error.value = cause.message || '操作失败，请刷新后重试'; notify?.(error.value, 'error'); } }
  finally { busy.value = false; busyAction.value = ''; }
}
watch([() => props.activityId, isLoggedIn, () => getAuth()?.memberId], load, { immediate: true });
watch(busy, value => emit('busy', value), { immediate: true });
defineExpose({ refresh: load });
onBeforeUnmount(() => { sequence++; });
</script>
<style scoped>
.wheel-participation { padding: 20px 0; border-top: 1px solid #e4e9f0; }
.wheel-participation h2 { margin: 0 0 16px; font-size: 20px; }
.wheel-participation p { color: #53647c; margin: 8px 0; line-height: 1.6; font-size: 14px; }
.wheel-summary, .wheel-prizes { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
.wheel-summary { padding: 12px; background: #f5f8fc; border-radius: 8px; }
.wheel-participation :deep(.activity-action) { margin: 12px 8px 0 0; min-height: 40px; padding: 8px 16px; background: #2585ef; border: 0; border-radius: 6px; color: #fff; font-size: 14px; cursor: pointer; }
.wheel-participation :deep(.activity-action:disabled) { opacity: .5; cursor: default; }
.wheel-assist { margin-top: 20px; }
.wheel-assist label { display: block; color: #53647c; font-size: 14px; }
.wheel-assist input { margin-top: 8px; min-height: 40px; width: 100%; box-sizing: border-box; border: 1px solid #d9e2ef; padding: 0 12px; border-radius: 6px; font-size: 14px; }

.wheel-participation :deep(.activity-action--secondary) { background:#eef7ff;color:#1676b9;border:1px solid #d5e5f5; }
.wheel-wallet { margin-top:16px; }.wheel-wallet label { display:block;margin-bottom:8px; }.wheel-wallet select { box-sizing:border-box;width:100%;min-height:44px;padding:8px 12px;border:1px solid #d9e2ef;border-radius:8px;background:#fff;color:#303442;font:inherit; }
</style>
